import { CheckCircle2, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { extractApiErrorMessage } from '@/lib/api-client';

import { useConnectEasyBillMutation } from '../api/use-connect-easybill-mutation';
import { resolveNoteKind } from '../lib/invoice-label';
import type { InvoiceRow } from '../types';

const NON_CONNECTABLE_STATUSES: ReadonlySet<string> = new Set(['Draft', 'Void']);

type IneligibleReason = 'buy-side' | 'credit-note' | 'not-issued';

// Data-driven reason map — one entry per blocked reason (the connected state
// and the eligible state are handled separately).
const INELIGIBLE_TOOLTIP: Record<IneligibleReason, string> = {
  'buy-side': 'Connect to EasyBill is available on sell-side invoices Metycle issues.',
  'credit-note':
    'Credit Notes cannot be connected to EasyBill; they would push as a positive invoice.',
  'not-issued': 'Issue the invoice before connecting it to EasyBill.',
};

function ineligibleReason(invoice: InvoiceRow): IneligibleReason | null {
  if (invoice.invoice_direction !== 'Sale') return 'buy-side';
  // Catches both the typed Credit_Note and a legacy Adjustment credit note.
  if (resolveNoteKind(invoice) === 'Credit') return 'credit-note';
  if (invoice.status === null || NON_CONNECTABLE_STATUSES.has(invoice.status)) {
    return 'not-issued';
  }
  return null;
}

export interface ConnectEasyBillButtonProps {
  readonly invoice: InvoiceRow;
  readonly compact?: boolean | undefined;
  readonly onConnected?: (() => void) | undefined;
}

function ConnectedBadge({ invoice }: Readonly<{ invoice: InvoiceRow }>) {
  return (
    <Badge
      variant="outline"
      className="border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
      data-testid={`invoice-${invoice.id}-easybill-connected`}
    >
      <CheckCircle2 className="mr-1 size-3" aria-hidden />
      Connected to EasyBill · #{invoice.easybill_document_id}
    </Badge>
  );
}

function IneligibleButton({
  invoiceId,
  reason,
  compactClass,
}: Readonly<{ invoiceId: string; reason: IneligibleReason; compactClass?: string | undefined }>) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled
              className={compactClass}
              aria-label="Connect to EasyBill"
              data-testid={`invoice-${invoiceId}-connect-easybill`}
            >
              <Link2 className="size-3" aria-hidden />
              Connect to EasyBill
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{INELIGIBLE_TOOLTIP[reason]}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Sell-side outbound "Connect to EasyBill" control. Renders the connected
// state when the invoice already carries an easybill_document_id, a
// disabled-with-tooltip button when the invoice is ineligible, and the live
// connect action otherwise. Mounted on sell-side rows (edit mode) and in the
// invoice detail drawer.
export function ConnectEasyBillButton({
  invoice,
  compact,
  onConnected,
}: ConnectEasyBillButtonProps) {
  const mutation = useConnectEasyBillMutation({
    invoiceId: invoice.id,
    blOrderId: invoice.bl_order_id ?? undefined,
  });
  const compactClass = compact === true ? 'h-7 gap-1 px-3 text-xs [&_svg]:size-3' : undefined;

  if (invoice.easybill_document_id !== null) return <ConnectedBadge invoice={invoice} />;

  const reason = ineligibleReason(invoice);
  if (reason !== null) {
    return <IneligibleButton invoiceId={invoice.id} reason={reason} compactClass={compactClass} />;
  }

  const onClick = () => {
    mutation.mutate(undefined, {
      onSuccess: (row) => {
        // Say which of the two things happened: attaching to the document that
        // already existed in EasyBill, or creating a fresh one there.
        toast.success(
          row.easybill_connect_mode === 'linked'
            ? `Linked to existing EasyBill document #${row.easybill_document_id ?? ''}.`
            : 'Created a new EasyBill document.',
        );
        onConnected?.();
      },
      onError: (err) => {
        toast.error(extractApiErrorMessage(err, 'Failed to connect to EasyBill.'));
      },
    });
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={compactClass}
      disabled={mutation.isPending}
      data-testid={`invoice-${invoice.id}-connect-easybill`}
      onClick={onClick}
    >
      {mutation.isPending ? (
        <Loader2 className="size-3 animate-spin" aria-hidden />
      ) : (
        <Link2 className="size-3" aria-hidden />
      )}
      Connect to EasyBill
    </Button>
  );
}
